import type { ColumnDef } from '@/lib/tanstack-table';
import { cn } from '@/lib/utils';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { getCartLineTableMeta as getMeta } from './cartLineTableMeta';
import { effectiveQuantity } from '../utils/lineQuantity';
import {
  AUTO_RELEASE_COLUMN_ID,
  SOURCE_AVAILABLE_COLUMN_ID,
} from '../utils/buyerBoardColumns';

/** Shown as "Mon d": the day a daily cap first held the line back. */
function heldSinceLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Transfer board line columns (GEN-3026): what the source can give for the
 * item, and whether its daily auto-release limit held the line back.
 */
export const transferLineColumns: ColumnDef<AssignmentLine>[] = [
  {
    id: SOURCE_AVAILABLE_COLUMN_ID,
    size: 88,
    accessorFn: (l) => l.sourceAvailableQuantity ?? -1,
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Source avail.', {
            context:
              'cart line table column header, stock the source location can give above its release floor',
          })}
        />
      );
    },
    cell: ({ row }) => {
      const available = row.original.sourceAvailableQuantity;
      if (available === null) return <span className="text-gray-300">—</span>;
      const short = available < effectiveQuantity(row.original);
      return (
        <span
          className={cn('tabular-nums', short && 'font-medium text-amber-700')}
        >
          {available.toLocaleString()}
        </span>
      );
    },
    meta: { align: 'right' },
  },
  {
    id: AUTO_RELEASE_COLUMN_ID,
    size: 96,
    accessorFn: (l) => l.autoReleaseQueuedAt ?? '',
    header: ({ column, table }) => {
      const meta = getMeta(table);
      return (
        <DataTableColumnHeader
          column={column}
          title={meta.gt('Auto release', {
            context:
              'cart line table column header, whether the daily auto-release limit held the line back',
          })}
        />
      );
    },
    cell: ({ row, table }) => {
      const queuedAt = row.original.autoReleaseQueuedAt;
      if (queuedAt === null) return <span className="text-gray-300">—</span>;
      const meta = getMeta(table);
      return (
        <span
          className="truncate rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800"
          title={meta.gt(
            'Held back by the source’s daily auto-release limit since {date}',
            {
              context: 'cart line auto release cell tooltip',
              date: heldSinceLabel(queuedAt),
            },
          )}
        >
          {meta.gt('Queued {date}', {
            context:
              'cart line auto release cell: held back by the daily limit since a date',
            date: heldSinceLabel(queuedAt),
          })}
        </span>
      );
    },
  },
];
